import {
  Box,
  Card,
  Fade,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { closeIcon } from "assets/images";
import CustomPagination from "components/customPagination";
import NoRecords from "components/NoRecords";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  EnrollmentAndAttendanceHistoryListPayload,
  EnrollmentAndAttendanceHistoryListSuccessPayload,
} from "store/student/types";
import {
  CLEAR_TOOLTIP_TITLE,
  DATE_VIEW_FORMAT,
  DEFAULT_PAGE_NO,
  DEFAULT_TEN_PAGE_SIZE,
  PREFERRED_COURSE_DELIVERY_SELECTION_REQUIRED,
  TERM_STATUS,
  TIME_VIEW_FORMAT,
} from "utils/constants/constant";
import { Order, OrderByOptions } from "utils/enums/sorting";
import {
  allowSearch,
  getPagePayloadValuesWithId,
  getStudentEnrolmentStatusCSSClass,
  getStudentEnrolmentStatusLabel,
  hideLoaderForBatchApiCall,
  onTextFieldKeyPress,
  showLoaderForBatchApiCall,
} from "utils/helper";
import {
  IEnhancedTablePropsForEnrolmentAndAttendanceHistory,
  IEnrolmentAndAttendanceData,
  IEnrolmentAndAttendanceHistoryDetailKeys,
  IEnrolmentAndAttendanceHistoryListMeta,
  IEnrolmentAndAttendanceHistoryPagePayloadValues,
} from "utils/interfaces/student";

const EnhancedTableHead = (
  props: IEnhancedTablePropsForEnrolmentAndAttendanceHistory
) => {
  const { order, orderBy, onRequestSort, fieldName, keyName, sxStyle } = props;
  const createSortHandler =
    (newOrderBy: keyof IEnrolmentAndAttendanceHistoryDetailKeys) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  return (
    <TableCell
      key={keyName}
      align="left"
      onClick={createSortHandler(keyName)}
      sortDirection={orderBy === keyName ? order : false}
      sx={sxStyle}
      className="term-sorting"
    >
      {fieldName}
      <Box component="span" className="sorting-icon" />
    </TableCell>
  );
};
interface IEnrolmentAndAttendanceHistoryProps {
  enrollmentAndAttendanceHistoryListRequest: (
    payload: EnrollmentAndAttendanceHistoryListPayload
  ) => {};
}

const EnrolmentAndAttendanceHistory: React.FC<IEnrolmentAndAttendanceHistoryProps> =
  ({ enrollmentAndAttendanceHistoryListRequest }) => {
    const location = useLocation<{ studentId: number }>();

    const [searchText, setSearchText] = useState<string>("");
    const [enrolmentAndAttendanceList, setEnrolmentAndAttendanceList] =
      useState<IEnrolmentAndAttendanceData[]>();
    const [enrolmentAndAttendanceListMeta, setEnrolmentAndAttendanceListMeta] =
      useState<IEnrolmentAndAttendanceHistoryListMeta>();
    const [page, setPage] = useState<string>(DEFAULT_TEN_PAGE_SIZE);
    const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);

    const [order, setOrder] = useState<Order>(OrderByOptions.Asc);
    const [orderBy, setOrderBy] =
      useState<keyof IEnrolmentAndAttendanceHistoryDetailKeys>("termStatus");

    const onEnrolmentAndAttendanceHistoryListSuccess = (
      response: EnrollmentAndAttendanceHistoryListSuccessPayload
    ) => {
      hideLoaderForBatchApiCall();
      setEnrolmentAndAttendanceList(
        response.enrollmentAndAttendanceHistoryList
      );
      setEnrolmentAndAttendanceListMeta(
        response.enrollmentAndAttendanceHistoryListPagination
      );
    };

    const getEnrolmentAndAttendanceHistoryList = (filtered?: boolean) => {
      const studentId = location.state?.studentId;
      if (enrollmentAndAttendanceHistoryListRequest && studentId) {
        showLoaderForBatchApiCall();
        const values: IEnrolmentAndAttendanceHistoryPagePayloadValues =
          getPagePayloadValuesWithId(studentId, orderBy, order, pageNo, page);
        values.search = filtered ? "" : searchText.trim();

        const payload = {
          values,
          callback: onEnrolmentAndAttendanceHistoryListSuccess,
        };
        enrollmentAndAttendanceHistoryListRequest(payload);
      }
    };

    useEffect(() => {
      getEnrolmentAndAttendanceHistoryList();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order, orderBy, pageNo, page]);

    const handleStudentSearch = (
      event: React.ChangeEvent<HTMLInputElement>
    ): void => {
      setSearchText(event.target.value);
    };

    const handleClearSearch = () => {
      setSearchText("");
      getEnrolmentAndAttendanceHistoryList(true);
    };

    const handleStudentSearchKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (allowSearch(e)) {
        if (pageNo === DEFAULT_PAGE_NO) getEnrolmentAndAttendanceHistoryList();
        else setPageNo(DEFAULT_PAGE_NO);
      }
      onTextFieldKeyPress(e, searchText?.length);
    };

    const handleRequestSort = useCallback(
      (
        event: React.MouseEvent<unknown>,
        newOrderBy: keyof IEnrolmentAndAttendanceHistoryDetailKeys
      ) => {
        const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
        const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
        setOrder(toggledOrder);
        setOrderBy(newOrderBy);
      },
      [order, orderBy]
    );

    const onPageChange = (e: React.ChangeEvent<unknown>, value: number) => {
      setPageNo(value);
    };

    const handleChange = (event: SelectChangeEvent) => {
      setPage(event.target.value as string);
      setPageNo(DEFAULT_PAGE_NO);
    };
    const getStatusText = (statusId: number) => {
      let statusText = "";
      TERM_STATUS?.forEach((status) => {
        if (status.value === statusId) {
          statusText = status.label;
        }
      });

      return statusText;
    };

    return (
      <>
        <Card>
          <Box className="table-card-header">
            <Box
              sx={{
                justifyContent: "space-between",
                width: "100%",
                alignItems: "center",
                flexWrap: "wrap",
                rowGap: "16px",
              }}
            >
              <Typography variant="h3">
                Enrolment and Attendance History
              </Typography>
              <TextField
                variant="outlined"
                id="search-box"
                className="search-input"
                placeholder="Search"
                onChange={handleStudentSearch}
                onKeyDown={handleStudentSearchKeyDown}
                value={searchText}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      sx={{ position: "absolute", right: "0" }}
                      position="end"
                    >
                      {searchText && (
                        <Tooltip
                          TransitionComponent={Fade}
                          TransitionProps={{ timeout: 600 }}
                          title={CLEAR_TOOLTIP_TITLE}
                          arrow
                        >
                          <IconButton edge="end" onClick={handleClearSearch}>
                            <img src={closeIcon} alt="close" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) => handleRequestSort(e, "termStatus")}
                    fieldName="Term Status"
                    keyName="termStatus"
                    sxStyle={{
                      maxWidth: "117px",
                      width: "117px",
                      minWidth: "117px",
                    }}
                  />
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) => handleRequestSort(e, "term")}
                    fieldName="Term"
                    keyName="term"
                    sxStyle={{
                      width: "75px",
                      minWidth: "75px",
                    }}
                  />
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) =>
                      handleRequestSort(e, "courseStartDate")
                    }
                    fieldName="Course start Date"
                    keyName="courseStartDate"
                    sxStyle={{
                      width: "121px",
                      minWidth: "121px",
                      textAlign: "center",
                    }}
                  />
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) => handleRequestSort(e, "dateEnrolled")}
                    fieldName="Date Enrolled"
                    keyName="dateEnrolled"
                    sxStyle={{
                      width: "114px",
                      minWidth: "114px",
                      textAlign: "center",
                    }}
                  />
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) => handleRequestSort(e, "location")}
                    fieldName="Location"
                    keyName="location"
                    sxStyle={{
                      width: "144px",
                      minWidth: "144px",
                      maxWidth: "144px",
                    }}
                  />
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) => handleRequestSort(e, "courseSession")}
                    fieldName="Course Session"
                    keyName="courseSession"
                    sxStyle={{
                      width: "145px",
                      minWidth: "145px",
                    }}
                  />
                  <TableCell sx={{ width: "150px", minWidth: "150px" }}>
                    Enrolled by
                  </TableCell>
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) => handleRequestSort(e, "timeOfSession")}
                    fieldName="Time of Session"
                    keyName="timeOfSession"
                    sxStyle={{
                      width: "145px",
                      minWidth: "145px",
                    }}
                  />
                  <TableCell
                    align="center"
                    sx={{ width: "108px", minWidth: "108px" }}
                  >
                    Attendance Rate
                  </TableCell>
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={(e) => handleRequestSort(e, "blendedCourse")}
                    fieldName="Blended Course"
                    keyName="blendedCourse"
                    sxStyle={{
                      width: "153px",
                      minWidth: "153px",
                      textAlign: "center",
                    }}
                  />
                  <TableCell
                    align="center"
                    sx={{ width: "143px", minWidth: "132px" }}
                  >
                    Enrolment Status
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {enrolmentAndAttendanceList?.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell
                      component="th"
                      scope="row"
                      className={`${getStatusText(
                        row?.termStatus
                      ).toLowerCase()} status`}
                    >
                      <div>{getStatusText(row?.termStatus)}</div>
                    </TableCell>
                    <TableCell align="center">{row?.term}</TableCell>
                    <TableCell align="center">
                      {moment(row?.courseStartDate).format(DATE_VIEW_FORMAT)}
                    </TableCell>
                    <TableCell align="center">
                      {moment(row?.dateEnrolled).format(DATE_VIEW_FORMAT)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row?.locationName}
                      </Typography>
                    </TableCell>
                    <TableCell className="break-word">
                      {row?.courseSession}
                    </TableCell>
                    <TableCell>{row?.enrolledBy || "-"}</TableCell>
                    <TableCell>
                      {moment(row?.startTimeOfSession).format(TIME_VIEW_FORMAT)}{" "}
                      to{" "}
                      {moment(row?.endTimeOfSession).format(TIME_VIEW_FORMAT)}
                    </TableCell>
                    <TableCell align="center">{row.attendanceRate}</TableCell>
                    <TableCell align="center">
                      {row.isBlended ? (
                        <>
                          {row.blendedCourse ??
                            PREFERRED_COURSE_DELIVERY_SELECTION_REQUIRED}
                        </>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      className={`${getStudentEnrolmentStatusCSSClass(
                        getStudentEnrolmentStatusLabel(row?.enrollmentStatus)
                      )} status`}
                    >
                      <div>
                        {getStudentEnrolmentStatusLabel(row?.enrollmentStatus)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {enrolmentAndAttendanceList &&
          enrolmentAndAttendanceList.length > 0 ? (
            <CustomPagination
              handleChange={handleChange}
              page={page}
              pageNo={pageNo}
              onPageChange={onPageChange}
              listMeta={enrolmentAndAttendanceListMeta}
            />
          ) : (
            <NoRecords />
          )}
          <Typography
            variant="body2"
            className="note"
            sx={{ marginTop: { xs: "20px", lg: "21px" } }}
          >
            Note: Successful attendance is defined as attending at least 75% of
            the course (by duration).
          </Typography>
        </Card>
      </>
    );
  };

export default EnrolmentAndAttendanceHistory;
